/**
 * Five Hundred bot.
 *
 * - Bidding: estimates the tricks the hand takes alone for every suit (and no-trump), adds an allowance for the partner,
 *   and makes the cheapest bid above the current one that the estimate supports. It leaves the auction to a partner who
 *   already holds the bid.
 * - Kitty: keeps trumps and high cards, discards spare low cards (preferably emptying a short suit).
 * - Play: partnership-aware heuristic — leads master cards (and draws trumps when declaring), takes tricks as cheaply as
 *   possible, lets a winning partner keep the trick, ruffs when void. NORMAL and above track the cards still unseen.
 * The bot only ever reads its own seat's view.
 */
import { isPrintedJoker, parseCard, type CardId, type Suit } from '../../core/cards.js';
import { chance, type BotContext, type BotEngine } from '../../bots/types.js';
import {
  fhAllContracts,
  fhBidValue,
  fhChooseDiscard,
  fhDeck,
  fhEffectiveSuit,
  fhIsTrump,
  fhPartnerOf,
  fhPower,
  fhTrickWinner,
  type FHSuit,
  type FHTrickPlay,
  type FiveHundredAction,
  type FiveHundredLegal,
  type FiveHundredView,
} from './engine.js';

const ESTIMATE_SCALE = 1.35;
/** Tricks a partner is assumed to add when deciding whether the team can make a contract. */
const PARTNER_ALLOWANCE = 1.0;

/** Tricks the hand is expected to take alone if `trump` is trumps (or no-trump). */
export function estimateFiveHundredTricks(hand: readonly CardId[], trump: FHSuit): number {
  let est = 0;
  const suitCards = new Map<string, CardId[]>();
  for (const c of hand) {
    const eff = fhEffectiveSuit(c, trump);
    suitCards.set(eff, [...(suitCards.get(eff) ?? []), c]);
  }
  if (trump === 'N') {
    for (const [suit, cards] of suitCards) {
      if (suit === 'J') {
        est += 1;
        continue;
      }
      const ranks = cards.map((c) => parseCard(c).rank).sort((a, b) => b - a);
      if (ranks.includes(14)) est += 0.9;
      if (ranks.includes(13)) est += ranks.length >= 2 ? 0.5 : 0.2;
      if (ranks.includes(12) && ranks.length >= 3) est += 0.25;
      if (ranks.length >= 5) est += (ranks.length - 4) * 0.5;
    }
    return est * ESTIMATE_SCALE;
  }
  const trumps = suitCards.get(trump) ?? [];
  for (const c of trumps) {
    const p = fhPower(c, trump);
    if (p >= 300) est += 1;
    else if (p >= 200) est += 0.95;
    else if (p >= 190) est += 0.9;
    else if (p === 14) est += 0.85;
    else if (p === 13) est += trumps.length >= 3 ? 0.7 : 0.4;
    else if (p === 12) est += trumps.length >= 4 ? 0.5 : 0.2;
  }
  if (trumps.length > 3) est += (trumps.length - 3) * 0.55;
  let voids = 0;
  for (const suit of ['S', 'C', 'D', 'H'] as const) {
    if (suit === trump) continue;
    const cards = suitCards.get(suit) ?? [];
    const ranks = cards.map((c) => parseCard(c).rank);
    if (ranks.includes(14)) est += 0.9;
    if (ranks.includes(13)) est += cards.length >= 2 && cards.length <= 4 ? 0.4 : 0.1;
    if (cards.length === 0) voids += 1;
    else if (cards.length === 1) voids += 0.5;
  }
  const spare = Math.max(0, trumps.length - 2);
  est += Math.min(voids, spare) * 0.6;
  return est * ESTIMATE_SCALE;
}

function chooseBid(view: FiveHundredView, legal: Extract<FiveHundredLegal, { kind: 'bid' }>, ctx: BotContext): FiveHundredAction {
  const me = view.seat!;
  if (view.highBid?.seat === fhPartnerOf(me)) return { type: 'pass' };
  const noise = ctx.difficulty === 'BEGINNER' ? (ctx.rng.next() - 0.5) * 2 : ctx.difficulty === 'EASY' ? (ctx.rng.next() - 0.5) : 0;
  const shade = ctx.difficulty === 'HARD' || ctx.difficulty === 'EXPERT' ? 0.15 : 0;
  const ests = new Map<FHSuit, number>();
  for (const s of ['S', 'C', 'D', 'H', 'N'] as const) ests.set(s, estimateFiveHundredTricks(view.hand, s) + PARTNER_ALLOWANCE + noise - shade);
  // For each trump choice, the most tricks the hand supports; bid the cheapest of those that beats the current bid.
  const options: { tricks: number; suit: FHSuit }[] = [];
  for (const s of ['S', 'C', 'D', 'H', 'N'] as const) {
    const tricks = Math.min(10, Math.floor(ests.get(s)!));
    if (tricks >= 6 && fhBidValue({ tricks, suit: s }) > legal.minValue) options.push({ tricks, suit: s });
  }
  options.sort((a, b) => fhBidValue(a) - fhBidValue(b));
  if (options.length > 0) {
    const pick = options[0]!;
    return { type: 'bid', tricks: pick.tricks, suit: pick.suit };
  }
  if (!legal.canPass) {
    const forced = fhAllContracts().find((c) => fhBidValue(c) > legal.minValue)!;
    return { type: 'bid', tricks: forced.tricks, suit: forced.suit };
  }
  return { type: 'pass' };
}

interface Knowledge {
  unseen: Set<CardId>;
}

function buildKnowledge(view: FiveHundredView, ctx: BotContext): Knowledge {
  const seen = new Set<CardId>(view.hand);
  view.trick.forEach((p) => seen.add(p.card));
  const n = view.tricks.length;
  view.tricks.forEach((t, i) => {
    if (ctx.profile.memory >= 1 || i >= n - Math.max(1, Math.round(n * ctx.profile.memory))) for (const p of t.plays) seen.add(p.card);
  });
  return { unseen: new Set(fhDeck().filter((c) => !seen.has(c))) };
}

function chooseCard(view: FiveHundredView, legal: CardId[], ctx: BotContext): { card: CardId; suit?: Suit } {
  const trump = view.trump!;
  const me = view.seat!;
  const partner = fhPartnerOf(me);
  const trick: FHTrickPlay[] = view.trick;
  const k = ctx.profile.memory > 0 ? buildKnowledge(view, ctx) : null;
  const eff = (c: CardId) => fhEffectiveSuit(c, trump);
  const power = (c: CardId) => (fhIsTrump(c, trump) ? 1000 : 0) + fhPower(c, trump);
  const lowest = (cards: CardId[]) => [...cards].sort((a, b) => power(a) - power(b))[0]!;
  const highest = (cards: CardId[]) => [...cards].sort((a, b) => power(b) - power(a))[0]!;
  const jokerSuit = (): Suit => {
    const counts = new Map<Suit, number>();
    for (const c of view.hand) if (!isPrintedJoker(c)) counts.set(parseCard(c).suit, (counts.get(parseCard(c).suit) ?? 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'S';
  };
  const out = (card: CardId) => (trump === 'N' && trick.length === 0 && isPrintedJoker(card) ? { card, suit: jokerSuit() } : { card });
  const isMaster = (c: CardId) => {
    if (!k) return false;
    const s = eff(c);
    for (const u of k.unseen) if (eff(u) === s && fhPower(u, trump) > fhPower(c, trump)) return false;
    return true;
  };
  const dump = (cards: CardId[]) => {
    const side = cards.filter((c) => !fhIsTrump(c, trump) && !isPrintedJoker(c));
    if (side.length === 0) return lowest(cards);
    const counts = new Map<string, number>();
    side.forEach((c) => counts.set(eff(c), (counts.get(eff(c)) ?? 0) + 1));
    const longest = [...counts.entries()].sort((a, b) => b[1] - a[1])[0]![0];
    return lowest(side.filter((c) => eff(c) === longest));
  };

  if (legal.length === 1) return out(legal[0]!);

  if (trick.length === 0) {
    const declarerTeam = view.declarer !== null && view.declarer % 2 === me % 2;
    const trumps = legal.filter((c) => fhIsTrump(c, trump));
    const side = legal.filter((c) => !fhIsTrump(c, trump) && !isPrintedJoker(c));
    if (declarerTeam && trump !== 'N' && trumps.length >= 3) {
      const top = k ? trumps.filter(isMaster) : trumps.filter((c) => fhPower(c, trump) >= 190);
      if (top.length) return out(highest(top));
    }
    const masters = k ? side.filter(isMaster) : side.filter((c) => parseCard(c).rank === 14);
    if (masters.length) return out(highest(masters));
    // In no-trump the joker is a sure trick: cash it late, when the hand is nearly played out.
    if (trump === 'N' && view.hand.length <= 4) {
      const joker = legal.find(isPrintedJoker);
      if (joker) return out(joker);
    }
    if (side.length) {
      const counts = new Map<string, number>();
      side.forEach((c) => counts.set(eff(c), (counts.get(eff(c)) ?? 0) + 1));
      const shortSuit = [...counts.entries()].sort((a, b) => a[1] - b[1])[0]![0];
      return out(lowest(side.filter((c) => eff(c) === shortSuit)));
    }
    return out(lowest(legal));
  }

  const ledSuit = view.ledSuit;
  const winnerSeat = fhTrickWinner(trick, trump, ledSuit);
  const last = trick.length === 3;
  const wins = (c: CardId) => fhTrickWinner([...trick, { seat: me, card: c }], trump, ledSuit) === me;
  if (winnerSeat === partner) {
    const winnerCard = trick.find((p) => p.seat === winnerSeat)!.card;
    if (last || !k || isMaster(winnerCard)) return out(dump(legal));
    const over = legal.filter((c) => wins(c) && !fhIsTrump(c, trump) && eff(c) === eff(winnerCard));
    if (over.length && fhPower(winnerCard, trump) < 11) return out(lowest(over));
    return out(dump(legal));
  }
  const winners = legal.filter(wins);
  if (winners.length === 0) return out(dump(legal));
  if (last) return out(lowest(winners));
  const safe = k ? winners.filter((c) => isMaster(c) || fhIsTrump(c, trump)) : winners;
  return out(lowest(safe.length ? safe : winners));
}

export const fiveHundredBot: BotEngine<FiveHundredView, FiveHundredLegal, FiveHundredAction> = {
  decideAction(view, legal, ctx) {
    if (legal.kind === 'bid') return chooseBid(view, legal, ctx);
    if (legal.kind === 'discard') return { type: 'discard', cards: fhChooseDiscard(view.hand, view.trump!) };
    if (legal.kind === 'play') return this.chooseCard!(view, legal, ctx);
    throw new Error('Bot asked to act without legal actions');
  },

  chooseBet(view, legal, ctx) {
    if (legal.kind !== 'bid') return this.handleTimeout(view, legal, ctx);
    return chooseBid(view, legal, ctx);
  },

  chooseCard(view, legal, ctx) {
    if (legal.kind !== 'play') return this.handleTimeout(view, legal, ctx);
    const cards = legal.cards;
    if (cards.length === 1) {
      return trumpJokerLead(view, cards[0]!);
    }
    if (chance(ctx, ctx.profile.mistakeRate)) return trumpJokerLead(view, cards[ctx.rng.int(cards.length)]!);
    const choice = chooseCard(view, cards, ctx);
    return { type: 'play', card: cards.includes(choice.card) ? choice.card : cards[0]!, ...(choice.suit ? { suit: choice.suit } : {}) };
  },

  evaluateState(view) {
    const me = view.seat;
    if (me === null) return 0;
    const team = me % 2;
    const diff = view.teamScores[team]! - view.teamScores[1 - team]!;
    return Math.max(0, Math.min(1, 0.5 + diff / 1000));
  },

  calculateRisk(view, legal) {
    if (legal.kind === 'bid') return Math.min(1, Math.max(0, (10 - estimateFiveHundredTricks(view.hand, 'S')) / 10));
    return 0;
  },

  handleTimeout(view, legal) {
    if (legal.kind === 'bid') {
      if (legal.canPass) return { type: 'pass' };
      const forced = fhAllContracts().find((c) => fhBidValue(c) > legal.minValue)!;
      return { type: 'bid', tricks: forced.tricks, suit: forced.suit };
    }
    if (legal.kind === 'discard') return { type: 'discard', cards: fhChooseDiscard(view.hand, view.trump!) };
    if (legal.kind === 'play') {
      const trump = view.trump!;
      const power = (c: CardId) => (fhIsTrump(c, trump) ? 1000 : 0) + fhPower(c, trump);
      return trumpJokerLead(view, [...legal.cards].sort((a, b) => power(a) - power(b))[0]!);
    }
    throw new Error('No legal action');
  },
};

/** A play action, naming a suit when the joker is led in no-trump. */
function trumpJokerLead(view: FiveHundredView, card: CardId): FiveHundredAction {
  if (view.trump === 'N' && view.trick.length === 0 && isPrintedJoker(card)) {
    const counts = new Map<Suit, number>();
    for (const c of view.hand) if (!isPrintedJoker(c)) counts.set(parseCard(c).suit, (counts.get(parseCard(c).suit) ?? 0) + 1);
    return { type: 'play', card, suit: [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 'S' };
  }
  return { type: 'play', card };
}
